import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PayGroup, PayGroupListFilters, PayGroupsResponse } from '../models/pay-groups.model';

@Injectable({ providedIn: 'root' })
export class PayGroupsService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch paginated pay groups
   */
  getPayGroups(
    pageNumber: number = 1,
    pageSize: number = 6,
    orderBy: string = 'name',
    filters?: PayGroupListFilters,
  ): Observable<PayGroupsResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('orderBy', orderBy);

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http.get<PayGroupsResponse>(`${this.baseUrl}/api/pay-groups`, { params }).pipe(
      catchError((err) => {
        console.error('Error fetching pay groups:', err);
        return of(null);
      }),
    );
  }

  /**
   * Get pay group by ID
   */
  getPayGroupById(id: number): Observable<PayGroup | null> {
    return this.http
      .get<{ status: string; data: PayGroup }>(`${this.baseUrl}/api/pay-groups/${id}`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching pay group by ID:', err);
          return of(null);
        }),
      );
  }

  /**
   * Create pay group
   */
  createPayGroup(payload: {
    name: string;
    description?: string;
    isOvertimeEligible: boolean;
    overtimeThresholdHours: number;
  }): Observable<boolean> {
    return this.http.post<{ status: string }>(`${this.baseUrl}/api/pay-groups`, payload).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Error creating pay group:', err);
        return of(false);
      }),
    );
  }

  /**
   * Update pay group
   */
  updatePayGroup(id: number, payload: Partial<PayGroup>): Observable<boolean> {
    return this.http.patch<{ status: string }>(`${this.baseUrl}/api/pay-groups/${id}`, payload).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Error updating pay group:', err);
        return of(false);
      }),
    );
  }

  /**
   * Delete pay group
   */
  deletePayGroup(id: number): Observable<boolean> {
    return this.http.delete<{ status: string }>(`${this.baseUrl}/api/pay-groups/${id}`).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Error deleting pay group:', err);
        return of(false);
      }),
    );
  }
}